import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { generateUUID } from "shared/lib/generateUUID"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import { FloorTypename, ProjectTypename } from "shared/types/Project"

import { useFloorMutations } from "entities/floors/model/useFloorMutations"
import { useDefaultProjectChildPosition } from "entities/projects/model/useDefaultProjectChildPosition"
import { FloorFormData } from "features/floor-management/types/FloorFormData"
import { useFloorForm } from "widgets/floor/floor-form/model/useFloorForm"

import { NewFloorOverlayModel } from "../NewFloorOverlay"
import { NewFloorOverlayConnectorProps } from "../NewFloorOverlayConnector"

export function useNewFloorOverlayModel({
  payload: { floor, projectID, hideProject }
}: NewFloorOverlayConnectorProps): NewFloorOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const { createFloor: createFloorMutation } = useFloorMutations()
  const [createFloor] = createFloorMutation
  const { defaultPosition } = useDefaultProjectChildPosition({
    projectID,
    childType: FloorTypename
  })

  const submitFloor = useEvent((floorFormData: FloorFormData) =>
    createFloor({
      floor: { ...floorFormData.floor, id: generateUUID() },
      displayPosition: floorFormData.displayPosition,
      parentType: ProjectTypename,
      projectID: floorFormData.projectID || projectID,
      parentID: floorFormData.parentID || projectID
    })
  )

  const { handleSubmit, isSubmitting } = useFormSubmit<FloorFormData>({
    onSubmit: submitFloor,
    onSuccess: closeOverlay
  })

  const floorFormModel = useFloorForm({
    floor,
    projectID,
    parentID: projectID,
    displayPosition: defaultPosition,
    formOptions: {
      hideProject,
      hideClarificationRequest: true,
      hideAttachments: true
    },
    onSubmit: handleSubmit
  })

  return {
    floorFormModel,
    isLoading: isSubmitting,
    isSubmitting,
    onCancel: closeOverlay
  }
}
